import type { MealCard, Order, OrderCampaign, OrderLine, PaymentMethod, PaymentSplit } from '../../types'

/**
 * Between the app's shape and the database's.
 *
 * Postgres columns are snake_case; the app is camelCase. More importantly,
 * `undefined` has no meaning in Postgres, a write carrying one is either
 * rejected or silently dropped. That exact bug once made a waiter's button do
 * nothing at all: the note was undefined, Firestore refused the write, and the
 * refusal reached only the console. Every optional field here ends in `?? null`
 * for that reason.
 */

export type OrderRow = Record<string, unknown>

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback)
const numOr = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback

/** Postgres may hand back bigint as a string; Number keeps the integer. */
const money = (v: unknown): number => Number(v ?? 0)

const optMoney = (v: unknown): number | undefined =>
  v === null || v === undefined ? undefined : Number(v)

export function orderToRow(tenantId: string, o: Partial<Order> & { id: string }): OrderRow {
  return {
    id: o.id,
    tenant_id: tenantId,
    receipt_no: o.receiptNo ?? '',
    day_key: o.dayKey,
    device_id: o.deviceId ?? '',
    created_at_local: o.createdAtLocal ?? Date.now(),
    updated_at_local: o.updatedAtLocal ?? null,
    silent: o.silent ?? false,
    cashier_uid: o.cashierUid,
    cashier_email: o.cashierEmail ?? '',
    table_name: o.tableName ?? null,
    opened_by_name: o.openedByName ?? null,
    by_names: o.byNames ?? [],
    opened_by_uid: o.openedByUid ?? null,
    lines: o.lines ?? [],
    item_count: o.itemCount ?? 0,
    total_kurus: o.totalKurus ?? 0,
    subtotal_kurus: o.subtotalKurus ?? null,
    discount_kurus: o.discountKurus ?? null,
    discount_percent: o.discountPercent ?? null,
    campaign: o.campaigns?.length ? o.campaigns : null,
    payment_method: o.paymentMethod ?? null,
    split: o.split ?? null,
    cash_received_kurus: o.cashReceivedKurus ?? null,
    change_kurus: o.changeKurus ?? null,
    sadaka_kurus: o.bahsisKurus ?? null,
    sadaka_payment_method: o.bahsisPaymentMethod ?? null,
    meal_card: o.mealCard ?? null,
    takeaway_no: o.takeawayNo ?? null,
    // Yalnız kiosk siparişinde (supabase/kiosk.sql); sütunlar yokken de yazılsın.
    ...(o.awaitingPayment ? { awaiting_payment: true } : {}),
    ...(o.source ? { source: o.source } : {}),
    print_at_till: o.printAtTill === true,
    note: o.note ?? null,
    // Yalnız doluyken: sütunlar (supabase/ara-odeme.sql) olmadan da yazılsın.
    ...(o.prepaidKurus ? { prepaid_kurus: o.prepaidKurus } : {}),
    ...(o.partialOf ? { partial_of: o.partialOf } : {}),
    status: o.status,
  }
}

/** Settlement fields only, what changes when payment is taken. */
export function settlementToRow(
  s: Partial<Order> & { receiptNo: string; paymentMethod: PaymentMethod },
): OrderRow {
  return {
    receipt_no: s.receiptNo,
    payment_method: s.paymentMethod,
    status: 'completed',
    total_kurus: s.totalKurus ?? 0,
    subtotal_kurus: s.subtotalKurus ?? null,
    discount_kurus: s.discountKurus ?? null,
    discount_percent: s.discountPercent ?? null,
    campaign: s.campaigns?.length ? s.campaigns : null,
    split: s.split ?? null,
    cash_received_kurus: s.cashReceivedKurus ?? null,
    change_kurus: s.changeKurus ?? null,
    sadaka_kurus: s.bahsisKurus ?? null,
    sadaka_payment_method: s.bahsisPaymentMethod ?? null,
    meal_card: s.mealCard ?? null,
    ...(s.printAtTill !== undefined && { print_at_till: s.printAtTill }),
    note: s.note ?? null,
    ...(s.prepaidKurus ? { prepaid_kurus: s.prepaidKurus } : {}),
    // Kiosk siparişi ödendi: mutfak tetiği şimdi fişleri düşürür.
    ...(s.awaitingPayment === false ? { awaiting_payment: false } : {}),
    item_count: s.itemCount ?? 0,
    lines: s.lines ?? [],
  }
}

/** orders.campaign: dizi; ilk gün tek nesne olarak yazılanlar da okunuyor. */
function toOrderCampaigns(v: unknown): OrderCampaign[] | undefined {
  const list = Array.isArray(v) ? v : v && typeof v === 'object' ? [v] : []
  const out = list
    .map((x) => x as Record<string, unknown>)
    .map((c) => ({ id: str(c.id), name: str(c.name), kurus: Number(c.kurus) }))
    .filter((c) => Number.isFinite(c.kurus) && c.kurus > 0)
  return out.length ? out : undefined
}

export function rowToOrder(r: OrderRow): Order {
  return {
    id: str(r.id),
    receiptNo: str(r.receipt_no),
    dayKey: str(r.day_key),
    deviceId: str(r.device_id),
    // The server's timestamp is never read by the UI, everything sorts and
    // displays by createdAtLocal, the device clock, which is what makes an
    // offline sale behave exactly like an online one.
    createdAt: null,
    createdAtLocal: numOr(r.created_at_local, 0),
    updatedAtLocal: typeof r.updated_at_local === 'number' ? r.updated_at_local : undefined,
    settledAt: typeof r.settled_at === 'string' ? Date.parse(r.settled_at) : undefined,
    silent: r.silent === true,
    cashierUid: str(r.cashier_uid),
    cashierEmail: str(r.cashier_email),
    tableName: str(r.table_name) || undefined,
    openedByName: str(r.opened_by_name) || undefined,
    byNames: Array.isArray(r.by_names) ? (r.by_names as string[]) : undefined,
    openedByUid: str(r.opened_by_uid) || undefined,
    lines: Array.isArray(r.lines) ? (r.lines as OrderLine[]) : [],
    itemCount: numOr(r.item_count, 0),
    totalKurus: money(r.total_kurus),
    subtotalKurus: optMoney(r.subtotal_kurus),
    discountKurus: optMoney(r.discount_kurus),
    discountPercent: optMoney(r.discount_percent),
    campaigns: toOrderCampaigns(r.campaign),
    paymentMethod: (str(r.payment_method) || undefined) as PaymentMethod | undefined,
    split: (r.split ?? undefined) as PaymentSplit | undefined,
    cashReceivedKurus: optMoney(r.cash_received_kurus),
    changeKurus: optMoney(r.change_kurus),
    bahsisKurus: optMoney(r.sadaka_kurus),
    bahsisPaymentMethod: (str(r.sadaka_payment_method) || undefined) as 'cash' | 'card' | undefined,
    mealCard: (str(r.meal_card) || undefined) as MealCard | undefined,
    takeawayNo: typeof r.takeaway_no === 'number' ? r.takeaway_no : undefined,
    awaitingPayment: r.awaiting_payment === true || undefined,
    source: r.source === 'kiosk' ? 'kiosk' : undefined,
    trackToken: str(r.track_token) || undefined,
    pickupCode: str(r.pickup_code) || undefined,
    printAtTill: r.print_at_till === true,
    note: str(r.note) || undefined,
    prepaidKurus: optMoney(r.prepaid_kurus),
    partialOf: str(r.partial_of) || undefined,
    mergedInto: str(r.merged_into) || undefined,
    status: str(r.status) as Order['status'],
    voidedBy: str(r.voided_by) || undefined,
  }
}
